import { FC, ReactNode, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Color, Icon } from "capitalrx-components-2.0/lib/general";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Subheading } from "capitalrx-components-2.0/lib/typography";

import { ExperienceContext } from "@/App/context";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { useNavigateClient } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";

import styles from "./styles.module.css";

const TRANSLATION_PATH = "DASHBOARD.recentActivityDashboard.quickActions";

interface QuickAction {
  labelKey: string;
  testId: string;
  renderIcon: ReactNode;
  onClick: () => void;
}

export const QuickActions: FC = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigateClient();

  const {
    hideMemberCard,
    hideFindBestPricePage,
    hideFormularyLookup,
    hideInboxPage,
    hideFormDocumentsPage,
    formularyLookupOverrideUrl,
  } = useContext(ExperienceContext);

  const { memberEligibility } = useContext(MemberEligibilityContext);

  const formularyUrl = formularyLookupOverrideUrl || memberEligibility?.formulary?.lookup_tool_url;

  const actions: QuickAction[] = useMemo(() => {
    const items: QuickAction[] = [];

    if (!hideMemberCard) {
      items.push({
        labelKey: `${TRANSLATION_PATH}.viewIdCard`,
        testId: "quick-action-id-card",
        renderIcon: <IDCardSVG />,
        onClick: () => {
          logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_quick_action_id_card");
          navigate("/member-id-card");
        },
      });
    }

    if (!hideFindBestPricePage) {
      items.push({
        labelKey: `${TRANSLATION_PATH}.findBestPrice`,
        testId: "quick-action-find-best-price",
        renderIcon: <Icon name="pill-bottle" color={Color.Turqoise[600]} />,
        onClick: () => {
          logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_quick_action_find_best_price");
          navigate("/find-best-price");
        },
      });
    }

    if (!hideFormularyLookup && formularyUrl) {
      items.push({
        labelKey: `${TRANSLATION_PATH}.viewFormulary`,
        testId: "quick-action-formulary",
        renderIcon: <ClipboardSVG />,
        onClick: () => {
          logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_quick_action_formulary");
          window.open(formularyUrl, "_blank", "noopener,noreferrer");
        },
      });
    }

    if (!hideInboxPage) {
      items.push({
        labelKey: `${TRANSLATION_PATH}.viewInbox`,
        testId: "quick-action-inbox",
        renderIcon: <InboxSVG />,
        onClick: () => {
          logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_quick_action_inbox");
          navigate("/inbox");
        },
      });
    }

    if (!hideFormDocumentsPage) {
      items.push({
        labelKey: `${TRANSLATION_PATH}.viewDocuments`,
        testId: "quick-action-documents",
        renderIcon: <Icon name="document" color={Color.Turqoise[600]} />,
        onClick: () => {
          logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_quick_action_documents");
          navigate("/forms-and-documents");
        },
      });
    }

    return items;
  }, [hideMemberCard, hideFindBestPricePage, hideFormularyLookup, hideInboxPage, hideFormDocumentsPage, formularyUrl]);

  if (actions.length === 0) {
    return null;
  }

  return (
    <div className={styles.GridWrapper}>
      {actions.map((action) => (
          <button
            key={action.labelKey}
            className={styles.QuickActionsCard}
            type="button"
            onClick={action.onClick}
            data-testid={action.testId}
          >
            <Shelf spacing="small">
              <div className={styles.IconContainer}>
                {action.renderIcon}
              </div>
              <Subheading>{translate(action.labelKey)}</Subheading>
              <Shelf.Spacer />
            </Shelf>
          </button>
      ))}
    </div>
  );
};

const ClipboardSVG = () => {
  return (
    <svg width="15" height="20" viewBox="0 0 15 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2.4H10.5H10.14C9.8625 1.03125 8.65125 0 7.2 0C5.74875 0 4.5375 1.03125 4.26 2.4H3.9H2.4C1.07625 2.4 0 3.47625 0 4.8V16.8C0 18.1238 1.07625 19.2 2.4 19.2H12C13.3237 19.2 14.4 18.1238 14.4 16.8V4.8C14.4 3.47625 13.3237 2.4 12 2.4ZM3 4.2V5.1C3 5.59875 3.40125 6 3.9 6H7.2H10.5C10.9987 6 11.4 5.59875 11.4 5.1V4.2H12C12.33 4.2 12.6 4.47 12.6 4.8V16.8C12.6 17.13 12.33 17.4 12 17.4H2.4C2.07 17.4 1.8 17.13 1.8 16.8V4.8C1.8 4.47 2.07 4.2 2.4 4.2H3ZM6.3 3C6.3 2.76131 6.39482 2.53239 6.5636 2.3636C6.73239 2.19482 6.9613 2.1 7.2 2.1C7.43869 2.1 7.66761 2.19482 7.8364 2.3636C8.00518 2.53239 8.1 2.76131 8.1 3C8.1 3.23869 8.00518 3.46761 7.8364 3.6364C7.66761 3.80518 7.43869 3.9 7.2 3.9C6.9613 3.9 6.73239 3.80518 6.5636 3.6364C6.39482 3.46761 6.3 3.23869 6.3 3ZM6.42375 8.82375C6.65625 8.59125 6.65625 8.20875 6.42375 7.97625C6.19125 7.74375 5.80875 7.74375 5.57625 7.97625L4.2 9.3525L3.72375 8.87625C3.49125 8.64375 3.10875 8.64375 2.87625 8.87625C2.64375 9.10875 2.64375 9.49125 2.87625 9.72375L3.77625 10.6237C4.00875 10.8562 4.39125 10.8562 4.62375 10.6237L6.42375 8.82375ZM7.2 10.2C7.2 10.53 7.47 10.8 7.8 10.8H10.2C10.53 10.8 10.8 10.53 10.8 10.2C10.8 9.87 10.53 9.6 10.2 9.6H7.8C7.47 9.6 7.2 9.87 7.2 10.2ZM6 13.8C6 14.13 6.27 14.4 6.6 14.4H10.2C10.53 14.4 10.8 14.13 10.8 13.8C10.8 13.47 10.53 13.2 10.2 13.2H6.6C6.27 13.2 6 13.47 6 13.8ZM4.2 14.7C4.43869 14.7 4.66761 14.6052 4.8364 14.4364C5.00518 14.2676 5.1 14.0387 5.1 13.8C5.1 13.5613 5.00518 13.3324 4.8364 13.1636C4.66761 12.9948 4.43869 12.9 4.2 12.9C3.9613 12.9 3.73239 12.9948 3.5636 13.1636C3.39482 13.3324 3.3 13.5613 3.3 13.8C3.3 14.0387 3.39482 14.2676 3.5636 14.4364C3.73239 14.6052 3.9613 14.7 4.2 14.7Z" fill="#00629C"/>
    </svg>
  );
};

const IDCardSVG = () => {
  return (
    <svg width="20" height="15" viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.5 1.875C17.8438 1.875 18.125 2.15625 18.125 2.5V12.5C18.125 12.8438 17.8438 13.125 17.5 13.125H2.5C2.15625 13.125 1.875 12.8438 1.875 12.5V2.5C1.875 2.15625 2.15625 1.875 2.5 1.875H17.5ZM2.5 0C1.12109 0 0 1.12109 0 2.5V12.5C0 13.8789 1.12109 15 2.5 15H17.5C18.8789 15 20 13.8789 20 12.5V2.5C20 1.12109 18.8789 0 17.5 0H2.5ZM3.125 10.9375C3.125 11.457 3.54297 11.875 4.0625 11.875H5.9375C6.45703 11.875 6.875 11.457 6.875 10.9375C6.875 10.418 6.45703 10 5.9375 10H4.0625C3.54297 10 3.125 10.418 3.125 10.9375ZM8.125 10.9375C8.125 11.457 8.54297 11.875 9.0625 11.875H10.9375C11.457 11.875 11.875 11.457 11.875 10.9375C11.875 10.418 11.457 10 10.9375 10H9.0625C8.54297 10 8.125 10.418 8.125 10.9375ZM13.125 10.9375C13.125 11.457 13.543 11.875 14.0625 11.875H15.9375C16.457 11.875 16.875 11.457 16.875 10.9375C16.875 10.418 16.457 10 15.9375 10H14.0625C13.543 10 13.125 10.418 13.125 10.9375ZM4.6875 3.75C4.16797 3.75 3.75 4.16797 3.75 4.6875V6.5625C3.75 7.08203 4.16797 7.5 4.6875 7.5H7.8125C8.33203 7.5 8.75 7.08203 8.75 6.5625V4.6875C8.75 4.16797 8.33203 3.75 7.8125 3.75H4.6875Z" fill="#00629C"/>
    </svg>
  );
};

const InboxSVG = () => {
  return (
    <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.78125 0C2.8625 0 2.0625 0.625 1.84063 1.51562L0.059375 8.6375C0.01875 8.79688 0 8.95937 0 9.12187V12C0 13.1031 0.896875 14 2 14H14C15.1031 14 16 13.1031 16 12V9.12187C16 8.95937 15.9812 8.79688 15.9406 8.6375L14.1594 1.51562C13.9375 0.625 13.1375 0 12.2188 0H3.78125ZM3.78125 2H12.2188L13.7188 8H12.1187C11.7406 8 11.3938 8.2125 11.225 8.55313L10.7781 9.44687C10.6094 9.78437 10.2625 10 9.88437 10H6.12187C5.74375 10 5.39687 9.7875 5.22813 9.44687L4.78125 8.55313C4.6125 8.21563 4.26562 8 3.8875 8H2.28125L3.78125 2Z" fill="#00629C"/>
    </svg>
  );
};
